import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { LoadingSpinnerComponent } from '../../custom/loading-spinner/loading-spinner.component';
import { Analytics, logEvent } from '@angular/fire/analytics';
import {
  CropsGradesCondition,
  DeliveryType,
  Documentation,
  MarketType,
  Origin,
  Specifications,
  Timeframe,
  SpecificationType,
  Units,
  futuresMonths,
  futuresYears,
} from '../lots.model';
import { NewLotOptionsService } from 'src/app/services/new-lot-options.service';
import { UserDetails } from '../../login/user.model';
import {
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormArray,
} from '@angular/forms';
import { locationValidator } from '../../custom/form-validators/location.validator';
import { priceValidator } from '../../custom/form-validators/price.validator';
import { Store } from '@ngrx/store';
import { selectUser } from 'src/app/store/user/user.selectors';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  map,
  Observable,
  switchMap,
  combineLatest,
  distinctUntilChanged,
  of,
  filter,
  debounceTime,
  delay,
  tap,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { SelectFilterComponent } from '../../custom/select-filter/select-filter.component';
import { selectTableSelectedMarket } from 'src/app/store/table-data/table-data.selectors';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MyErrorStateMatcher } from '../lot-new/new-lot.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { changeTableMarket } from 'src/app/store/table-data/table-data.actions';
import { DestinationSearchService } from 'src/app/services/destination-search.service';
import { AutocompleteComponent } from '../../custom/autocomplete/autocomplete.component';
import { MatDialog } from '@angular/material/dialog';
import { NewLotSummaryDialogComponent } from '../lot-new/new-lot-summary-dialog/new-lot-summary-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { LogEventsEnum } from 'src/app/firebase.model';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CreateLotService } from 'src/app/services/create-lot.service';
import { UserService } from 'src/app/services/user.service';
import { LotDetailsService } from 'src/app/services/lot-details.service';
interface FeildValueOptions {
  commodities: CropsGradesCondition[];
  deliveryTypes: DeliveryType[];
  origins: Origin[];
  units: Units[];
}

@Component({
  selector: 'app-new-bid-offer',
  providers: [provideNativeDateAdapter()],
  imports: [
    LoadingSpinnerComponent,
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    FormsModule,
    SelectFilterComponent,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDatepickerModule,
    AutocompleteComponent,
    MatCheckboxModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './new-bid-offer.component.html',
  styleUrl: './new-bid-offer.component.css',
})
export class NewBidOfferComponent implements OnInit {
  private analytics = inject(Analytics);
  private newLotOptionsService = inject(NewLotOptionsService);
  private createLotService = inject(CreateLotService);
  private searchDestinationsService = inject(DestinationSearchService);
  private userService = inject(UserService);
  private lotDetailsService = inject(LotDetailsService);
  private fb = inject(FormBuilder);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);

  horizontalPosition: MatSnackBarHorizontalPosition = 'center';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  // for delivery window control
  matcher = new MyErrorStateMatcher();
  minDate: Date;

  selectedCountry: string | undefined;
  controlTypes = SpecificationType;

  isLoading = signal<boolean>(true);

  basisMonths: { label: string; value: string }[];
  basisYears: number[];
  basisQuote = signal<string>(null);
  lotType = signal<string>(null);
  isRepost = signal<boolean>(false);

  searchingLocation = signal<boolean>(false);
  destinationsOptions = signal<{ name: string }[]>([]);
  recentlySearchedLocations = signal<string[]>([]);
  favouriteCommodities = signal<string[]>([]);

  // data repsonse

  fieldOptions = signal<FeildValueOptions>(null);

  userDetails = signal<UserDetails>(null);

  lotFormMarket = signal<MarketType>(null);

  // select dropdown control field options
  volumeUnits = signal<Units[]>([]);
  commodities = signal<string[]>([]);
  priceTypes = signal<string[]>([]);
  productTypes = signal<string[] | null>([]);
  grades = signal<string[] | null>([]);
  specifications = signal<Specifications[]>([]);
  documentations = signal<Documentation[] | null>(null);
  deliveryType = signal<DeliveryType>(null);
  timeFrameOptions = computed(() =>
    this.lotFormMarket() === MarketType.Farm
      ? [Timeframe.Day, Timeframe.GTC, Timeframe.Week]
      : [Timeframe.Day]
  );
  // Indications only exist on the Export and Track markets
  showIndication = computed(
    () =>
      this.lotFormMarket() === MarketType.Export ||
      this.lotFormMarket() === MarketType.Track
  );

  formFieldOptions$: Observable<{ data: FeildValueOptions; loading: boolean }>;

  newLotForm = this.fb.group(
    {
      lot_type: this.fb.control(null),
      origin: this.fb.control(null),
      commodity: this.fb.control(''), // Will be set by user details or default
      product_type: this.fb.control(null),
      grade: this.fb.control(null),
      specifications: this.fb.array([]),
      documentations: this.fb.array([]),
      price_type: this.fb.control(null),
      priceControl: this.fb.control(null, [
        Validators.pattern(/^\d+(\.\d{1,4})?$/),
      ]),
      basis_price: this.fb.group({
        basisPoints: this.fb.control(0, [
          Validators.pattern(/^-?\d+(\.\d{0,4})?$/),
        ]),
        month: this.fb.control(''),
        product_code: this.fb.control(''),
        year: this.fb.control(0),
      }),
      price_units: this.fb.control('', [Validators.required]),
      currency: this.fb.control(null),
      volume_value: this.fb.control('', [
        Validators.required,
        Validators.pattern(/^[1-9]\d*$/),
      ]),
      units: this.fb.control('MT'),
      destination: this.fb.control('', [
        Validators.required,
        locationValidator(),
      ]),
      delivery_type: this.fb.control(null),
      packaging: this.fb.control(null),

      time_frame: this.fb.control(null, Validators.required),
      additional_info: this.fb.control(null),
      payment_term: this.fb.control(null),
      container_type: this.fb.control(null),
      delivery_option: this.fb.control(null),
      market_type: this.fb.control(this.lotFormMarket(), Validators.required),
      tolerance: this.fb.control(null),
      rules_to_govern: this.fb.control(null, Validators.required),
      weight_grade_to_govern: this.fb.control(null, Validators.required),
      indication: this.fb.control(false),

      deliveryWindowControl: this.fb.group({
        start: this.fb.control(null, Validators.required),
        end: this.fb.control(null, Validators.required),
      }),
    },
    { validators: [priceValidator('priceControl', 'basis_price')] }
  );

  constructor() {
    this.basisMonths = futuresMonths;
    this.basisYears = futuresYears();
  }

  ngOnInit(): void {
    logEvent(this.analytics, LogEventsEnum.lots_create_view);
    this.recentlySearchedLocations.set(
      this.userService.getRecentlySearchedLocations()
    );
    this.controlChanges();
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);
    this.formFieldOptions$ = this.fetchData();
  }

  onSubmit() {
    // console.log('submit form', this.newLotForm.value);
    if (!this.newLotForm.valid) {
      this.snackBar.open('Invalid Field(s)', 'Close', {
        duration: 5000,
        panelClass: ['blue-snackbar'],
      });
      this.newLotForm.markAllAsTouched();
      return;
    }
    logEvent(this.analytics, LogEventsEnum.lots_summary_submit_tap);
    const formValues = this.newLotForm.value;
    const dialogRef = this.dialog.open(NewLotSummaryDialogComponent, {
      data: {
        formValues: {
          ...formValues,
          price:
            formValues.price_type === 'Cash'
              ? { amount: formValues.priceControl }
              : formValues.basis_price,
          documentations:
            formValues.documentations.length > 0
              ? formValues.documentations
              : null,
        },
      },
      width: '640px',
      maxWidth: '95vw',
      disableClose: true,
    });
    const data = {
      ...formValues,
      ship_begin: formValues.deliveryWindowControl.start,
      ship_end: formValues.deliveryWindowControl.end,
      cash_price: { amount: formValues.priceControl },
      additional_info: formValues.additional_info ?? '',
      grade: formValues.grade ?? '-',
    };
    delete data.deliveryWindowControl;
    delete data.priceControl;
    // Only send the price shape matching the selected price type
    if (formValues.price_type === 'Basis') {
      delete data.cash_price;
    } else {
      delete data.basis_price;
    }
    if (!data.documentations?.length) {
      delete data.documentations;
    }
    data.specifications = (data.specifications ?? []).filter(
      (spec: { title: string; value: string | null }) => spec.value !== null
    );
    const input = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== null)
    ) as typeof data;

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirm) {
        this.createLotService.mutate({ input }).subscribe({
          next: (response) => {
            const lotResponse = response.data?.createLot; // Correctly access the nested data
            if (lotResponse && lotResponse._id) {
              // add searched location to recently searched
              this.userService.addRecentlySearchedLocation(
                lotResponse.destination
              );
              this.router.navigate(['app/lots']);
            }
          },
          error: (error) => {
            // console.error('Error creating lot:', error);
            console.log('error in form', formValues);
            this.snackBar.open(
              `Error creating ${this.lotType}: ${error.message
                .toString()
                .substring(0, 35)}...`,
              'close',
              {
                duration: 5000,
                panelClass: 'blue-snackbar',
              }
            );
          },
        });
      }
    });
  }

  controlChanges() {
    this.newLotForm
      .get('origin')
      .valueChanges.pipe(
        map((val) => {
          if (val && this.fieldOptions()) {
            const comms = this.fieldOptions()
              .commodities.filter((e) => e.origin === val)
              .map((e) => e.commodity);

            const filteredFavComms: string[] = comms.filter((e) =>
              this.favouriteCommodities().includes(e)
            );
            this.favouriteCommodities.set(filteredFavComms);
            this.commodities.set(comms);
            this.newLotForm
              .get('commodity')
              .patchValue(filteredFavComms[0] ?? comms[0]);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.newLotForm
      .get('commodity')
      .valueChanges.pipe(
        switchMap((val) => {
          // Same commodity name can exist for several origins (e.g. Barley in
          // Canada and USA) with different product types/grades, so the lookup
          // must match the selected origin. The pair may also no longer exist
          // in the fetched options (e.g. reposting a retired commodity).
          const currentOrigin = this.newLotForm.get('origin').value;
          const cropGradesConditions = val
            ? this.fieldOptions()?.commodities.find(
                (e) => e.commodity === val && e.origin === currentOrigin
              )
            : null;
          if (cropGradesConditions) {
            const productTypes = (cropGradesConditions.product_types ?? [])
              .map((e) => e.product_type)
              .filter((t) => t != null);
            this.productTypes.set(productTypes.length ? productTypes : null);
            this.priceTypes.set([...(cropGradesConditions.price_types ?? [])]);
          }
          return of(cropGradesConditions);
        }),
        delay(0), // Waits for next event loop tick (microtask)
        map((cropGradesConditions) => {
          if (!cropGradesConditions) {
            return;
          }
          this.newLotForm
            .get('product_type')
            .setValue(
              cropGradesConditions.product_types?.[0]?.product_type ?? null
            );
          this.newLotForm
            .get('price_type')
            .setValue(cropGradesConditions.price_types?.[0] ?? null);
          if (cropGradesConditions.basis_product_code) {
            this.newLotForm.get('basis_price').patchValue({
              basisPoints: 0,
              month: this.basisMonths[0].value,
              year: this.basisYears[0],
              product_code: cropGradesConditions.basis_product_code,
            });
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.newLotForm
      .get('basis_price')
      .valueChanges.pipe(
        map((res) => {
          this.basisQuote.set(
            `${res.basisPoints}${res.product_code}${res.month}${res.year}`
          );
          this.newLotForm.updateValueAndValidity();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.newLotForm
      .get('product_type')
      .valueChanges.pipe(
        switchMap((val) => {
          const currentCropType = this.newLotForm.get('commodity').value;
          const currentOrigin = this.newLotForm.get('origin').value;

          const cropGradesConditions = this.fieldOptions().commodities.find(
            (e) =>
              e.commodity === currentCropType && e.origin === currentOrigin
          );
          let grades: string[] | null = null;
          if (val) {
            if (cropGradesConditions) {
              const product = cropGradesConditions.product_types?.find(
                (e) => e.product_type === val
              );
              grades =
                product?.grades_conditions?.map((e) => e.grade) ?? null;
              this.grades.set(grades);
            }
          } else {
            this.productTypes.set(null);

            // If prodcutType is null, get first entry in array of cropGradesConditions
            grades =
              cropGradesConditions?.product_types?.[0]?.grades_conditions?.map(
                (e) => e.grade
              ) ?? null;

            this.grades.set(grades);
          }
          return of({ grades });
        }),
        delay(0),
        map(({ grades }) => {
          this.newLotForm.get('grade').setValue(grades?.[0] ?? null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.newLotForm
      .get('grade')
      .valueChanges.pipe(
        switchMap((val) => {
          const currentCropType = this.newLotForm.get('commodity').value;
          const currentProduct = this.newLotForm.get('product_type').value;
          const currentGrade = this.newLotForm.get('grade').value;
          const currentOrigin = this.newLotForm.get('origin').value;

          const cropGradesConditions = this.fieldOptions().commodities.find(
            (e) =>
              e.commodity === currentCropType && e.origin === currentOrigin
          );

          if (cropGradesConditions) {
            const productType = cropGradesConditions.product_types?.find(
              (e) => e.product_type === currentProduct
            );
            if (productType) {
              const gradeConditions = productType.grades_conditions?.find(
                (e) => e.grade === currentGrade
              );
              if (gradeConditions) {
                const specs = gradeConditions.specifications ?? [];
                this.specifications.set(specs);
                this.setFormSpecifications(specs);
              } else {
                this.grades.set([]);
              }
            } else {
              // No matching product type — fall back to the first one's specs
              const specs =
                cropGradesConditions.product_types?.[0]?.grades_conditions?.[0]
                  ?.specifications ?? [];
              this.specifications.set(specs);

              if (specs.length) {
                this.setFormSpecifications(specs);
              }
            }
          }
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.newLotForm
      .get('destination')
      .valueChanges.pipe(
        debounceTime(400),
        filter((val) => val !== '' && val.length > 2),
        switchMap((res) => {
          this.searchingLocation.set(true);
          return this.searchDestinationsService.watch({ input: res })
            .valueChanges;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (rez) => {
          const arr =
            rez.data.searchLocations.map((d) => ({ name: d.display_name })) ||
            [];
          this.destinationsOptions.set(arr);
          this.searchingLocation.set(false);
        },
      });

    this.newLotForm
      .get('delivery_type')
      .valueChanges.pipe(
        switchMap((val) => {
          const deliveryType =
            this.fieldOptions()?.deliveryTypes.find((e) => e.name === val) ??
            null;
          this.deliveryType.set(deliveryType);
          this.documentations.set(deliveryType?.documentations ?? null);
          this.setFormDocumentations(this.documentations());
          return of(null);
        }),
        delay(0),
        map(() => {
          if (!this.deliveryType()) {
            return;
          }
          if (this.deliveryType().delivery_options) {
            this.newLotForm
              .get('delivery_option')
              .patchValue(this.deliveryType().delivery_options[0]);
          }

          if (this.deliveryType().container_types) {
            this.newLotForm
              .get('container_type')
              .setValue(this.deliveryType().container_types[0]);
          }

          if (this.deliveryType().packaging_types) {
            this.newLotForm
              .get('packaging')
              .setValue(this.deliveryType().packaging_types[0]);
          }

          if (this.deliveryType().tolerances) {
            this.newLotForm
              .get('tolerance')
              .setValue(this.deliveryType().tolerances[0]);
          }
          if (this.deliveryType().weight_grade_to_govern) {
            this.newLotForm
              .get('weight_grade_to_govern')
              .setValue(this.deliveryType().weight_grade_to_govern[0]);
          }
          if (this.deliveryType().payment_terms) {
            this.newLotForm
              .get('payment_term')
              .setValue(this.deliveryType().payment_terms[0]);
          }
          if (this.deliveryType().rules_to_govern) {
            this.newLotForm
              .get('rules_to_govern')
              .setValue(this.deliveryType().rules_to_govern[0]);
          }
          if (this.deliveryType().price_units) {
            this.newLotForm
              .get('price_units')
              .setValue(this.deliveryType().price_units[0]);
          }
          if (this.deliveryType().currencies) {
            let curr = '';
            if (
              this.lotFormMarket() === MarketType.Track ||
              this.lotFormMarket() === MarketType.Export
            ) {
              curr = this.deliveryType().currencies.find((e) =>
                e.includes('USD')
              );
            } else {
              curr = this.deliveryType().currencies.find((e) =>
                e.includes('CAD')
              );
            }
            this.newLotForm
              .get('currency')
              .setValue(curr ?? this.deliveryType().currencies[0]);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private fetchData(): Observable<{
    data: FeildValueOptions;
    loading: boolean;
  }> {
    return combineLatest([
      this.store.select(selectUser),
      this.store.select(selectTableSelectedMarket),
      this.activatedRoute.paramMap,
    ]).pipe(
      distinctUntilChanged(),
      switchMap(([user, stateMarket, params]) => {
        this.isLoading.set(true);
        const repostLotId = params.get('id');

        if (repostLotId) {
          return this.lotDetailsService
            .watch({ itemId: repostLotId })
            .valueChanges.pipe(
              map(({ data }) => ({
                user,
                market: MarketType[data.lot.market_type],
                lotData: data.lot,
                isRepost: true,
              }))
            );
        } else {
          const pathSegments = this.activatedRoute.snapshot.url.map(
            (s) => s.path
          );
          const lastSegment = pathSegments[pathSegments.length - 1];

          const type =
            lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
          this.newLotForm.get('lot_type').setValue(type);
          this.lotType.set(type);

          return of({
            user,
            market: stateMarket,
            lotData: null,
            isRepost: false,
          });
        }
      }),
      tap((context) => {
        const { user, market, isRepost, lotData } = context;
        if (user) {
          this.userDetails.set(user);
          this.favouriteCommodities.set([...user.favorable_commodities]);
        }
        this.lotFormMarket.set(market);
        this.newLotForm
          .get('market_type')
          ?.patchValue(market, { emitEvent: false });
        if (!this.showIndication()) {
          this.newLotForm.get('indication').setValue(false, {
            emitEvent: false,
          });
        }
        if (isRepost && !!lotData) {
          this.isRepost.set(true);
          this.lotType.set(lotData.lot_type);
        }
        return of({ market, lotData });
      }),
      switchMap(({ market, lotData }) => {
        return this.newLotOptionsService
          .watch({ market })
          .valueChanges.pipe(map((res) => ({ res, lotData })));
      }),
      map(({ res, lotData }) => {
        // console.log('fetched data', res);
        // Set field options FIRST
        this.volumeUnits.set(res.data.units);
        this.fieldOptions.set({
          commodities: res.data.commodities,
          deliveryTypes: res.data.deliveryTypes,
          origins: res.data.origins,
          units: res.data.units,
        });

        return {
          data: {
            commodities: res.data.commodities,
            deliveryTypes: res.data.deliveryTypes,
            origins: res.data.origins,
            units: res.data.units,
          },
          loading: res.loading,
          lotData,
        };
      }),
      tap((result) => {
        const { lotData, loading } = result;

        // NOW populate form after options are set
        if (lotData) {
          this.snackBar.open(
            `Review your ${this.lotType()} before reposting`,
            'close',
            {
              horizontalPosition: this.horizontalPosition,
              verticalPosition: this.verticalPosition,
              panelClass: ['blue-snackbar'],
              duration: 3000,
            }
          );
          this.populateFormWithLotDetails(lotData);
        } else {
          this.setInitialFormValues();
        }
        this.isLoading.set(loading);
      }),
      map(({ data, loading }) => ({ data, loading })),
      takeUntilDestroyed(this.destroyRef)
    );
  }
  private setInitialFormValues() {
    this.newLotForm.patchValue({
      origin: this.userDetails().default_origin,
      time_frame: this.timeFrameOptions()[0],
      delivery_type: this.fieldOptions().deliveryTypes[0].name,
      units: this.fieldOptions().units[0].name_short,
    });
  }

  private populateFormWithLotDetails(lot: any) {
    const patch: any = {
      lot_type: lot.lot_type,
      origin: lot.origin,
      commodity: lot.commodity,
      product_type: lot.product_type,
      grade: lot.grade,
      price_type: lot.price.__typename === 'CashPrice' ? 'Cash' : 'Basis',
      priceControl:
        lot.price.__typename === 'CashPrice' ? lot.price.amount : null,
      basis_price:
        lot.price.__typename === 'BasisPrice'
          ? {
              basisPoints: lot.price.basisPoints,
              month: lot.price.month,
              product_code: lot.price.product_code,
              year: lot.price.year,
            }
          : null,
      price_units: lot.price_units,
      currency: lot.currency,
      volume_value: lot.volume_value,
      units: lot.units,
      destination: lot.destination,
      delivery_type: lot.delivery_type,
      packaging: lot.packaging,
      time_frame: lot.time_frame,
      additional_info: lot.additional_info,
      payment_term: lot.payment_term,
      container_type: lot.container_type,
      delivery_option: lot.delivery_option,
      market_type: lot.market_type,
      tolerance: lot.tolerance,
      rules_to_govern: lot.rules_to_govern,
      weight_grade_to_govern: lot.weight_grade_to_govern,
      indication: lot.indication,
      deliveryWindowControl: {
        start: new Date(lot.ship_begin),
        end: new Date(lot.ship_end),
      },
    };
    const comms = this.fieldOptions()
      .commodities.filter((e) => e.origin === lot.origin)
      .map((e) => e.commodity);

    const filteredFavComms: string[] = comms.filter((e) =>
      this.favouriteCommodities().includes(e)
    );
    this.favouriteCommodities.set(filteredFavComms);
    this.commodities.set(comms);
    // The lot being reposted may reference catalog entries that no longer
    // exist (retired commodity, renamed product type/grade) — degrade
    // gracefully instead of breaking the whole prefill
    const cropGradesConditions = this.fieldOptions().commodities.find(
      (e) => e.commodity === lot.commodity && e.origin === lot.origin
    );
    const productTypes = cropGradesConditions?.product_types
      ?.map((e) => e.product_type)
      .filter((t) => t != null);

    this.productTypes.set(productTypes?.length ? productTypes : null);
    this.priceTypes.set([...(cropGradesConditions?.price_types ?? [])]);
    const product = cropGradesConditions?.product_types?.find(
      (e) => e.product_type === lot.product_type
    );
    this.grades.set(
      product?.grades_conditions?.map((e) => e.grade) ?? null
    );

    const selectedDeliveryType = this.fieldOptions().deliveryTypes.find(
      (dt) => dt.name === lot.delivery_type
    );
    if (selectedDeliveryType) {
      this.deliveryType.set(selectedDeliveryType);
      this.documentations.set(selectedDeliveryType.documentations);
    }
    this.newLotForm.patchValue(patch, { emitEvent: false });

    // Handle FormArrays
    if (lot.specifications) {
      this.setFormSpecifications(lot.specifications);
    }
    if (lot.documentations) {
      // The lot's saved docs only carry title/value — merge them into the
      // catalog docs so type/options survive (the template renders nothing
      // for docs without a type)
      const catalogDocs = this.documentations();
      const mergedDocs = (catalogDocs ?? []).map((doc) => ({
        ...doc,
        value:
          lot.documentations.find(
            (d: { title: string; value: string }) => d.title === doc.title
          )?.value ?? doc.defaultValue,
      }));
      this.setFormDocumentations(
        mergedDocs.length ? mergedDocs : lot.documentations
      );
    }
    // Mark the form as dirty and touched to ensure validation re-runs and styling applies
    this.newLotForm.markAllAsTouched();
    this.newLotForm.markAsDirty();
  }

  private setFormSpecifications(specs: Specifications[]) {
    const specsArray = this.newLotForm.get('specifications') as FormArray;
    const sameShape =
      specsArray.length === specs.length &&
      specs.every(
        (spec, i) => specsArray.at(i).get('title').value === spec.title
      );

    if (sameShape) {
      // Reuse the existing controls so @for (track spec) keeps its DOM rows (NG0956)
      specs.forEach((spec, i) => {
        const valueControl = specsArray.at(i).get('value');
        valueControl.setValidators(
          spec.isRequired ? Validators.required : null
        );
        valueControl.setValue(spec['value'] ?? spec.defaultValue);
      });
    } else {
      specsArray.clear();
      specs.forEach((spec) => {
        specsArray.push(
          this.fb.group({
            title: this.fb.control(spec.title),
            value: this.fb.control(
              spec['value'] ?? spec.defaultValue,
              spec.isRequired ? Validators.required : null
            ),
          })
        );
      });
    }
    specsArray.markAsDirty();
  }

  private setFormDocumentations(docs: Documentation[]) {
    const docsArray = this.newLotForm.get('documentations') as FormArray;
    if (docs !== null) {
      const sameShape =
        docsArray.length === docs.length &&
        docs.every(
          (doc, i) => docsArray.at(i).get('title').value === doc.title
        );

      if (sameShape) {
        // Reuse the existing controls so @for (track doc) keeps its DOM rows (NG0956)
        docs.forEach((doc, i) => {
          docsArray
            .at(i)
            .get('value')
            .setValue(doc['value'] ?? doc.defaultValue);
        });
      } else {
        docsArray.clear();
        docs.forEach((doc) => {
          docsArray.push(
            this.fb.group({
              title: this.fb.control<string>(doc.title),
              value: this.fb.control<string>(doc['value'] ?? doc.defaultValue),
            })
          );
        });
      }
      const properDocs = docs.map((doc) => ({
        title: doc.title,
        defaultValue: doc['value'] ?? doc.defaultValue,
        type: doc.type ?? null,
        options: doc.options ?? null,
        isRequired: doc.isRequired ?? false,
        order: doc.order ?? 0,
      }));
      this.documentations.set(properDocs);
    } else {
      docsArray.clear();
      this.documentations.set(null);
    }
  }

  onMarketChange(market: MarketType) {
    this.store.dispatch(changeTableMarket({ marketType: market }));
  }

  cancel() {
    this.router.navigate(['app/lots']);
  }
}
